import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';

import { NamedLink } from '../../../components';
import { batchCategoryListingsCount } from '../../../util/api';
import { useParallax } from '../../../hooks/useParallax';



import raceCarsImg2 from '../../../assets/optimized/raceCar2.webp';
import roadCarsIm2 from '../../../assets/optimized/testcheck1.webp';
import newUsedPartsImg2 from '../../../assets/optimized/testPart.webp';
import booksArtImg2 from '../../../assets/optimized/booksandart.webp';
import porscheWatchesImg2 from '../../../assets/optimized/watchesNew.webp';
import clothingImg2 from '../../../assets/optimized/clothingNew.webp';
import modelToysImg2 from '../../../assets/optimized/modelAndToy.webp';
import memorabiliaImg2 from '../../../assets/optimized/memories.webp';

import css from './FeaturedListing.module.css';

// Lightweight parallax item component
const ParallaxItem = ({ image, alt, children }) => {
  const { elementRef, offset } = useParallax(0.9);

  return (
    <div ref={elementRef} className={css.parallaxContainer}>
      <div className={css.imageWrapper}>
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className={css.parallaxImage}
          style={{
            transform: `translateY(${offset}px)`,
            willChange: 'transform',
          }}
        />
      </div>
      {children}
    </div>
  );
};

const FeaturedListing = props => {
  const { intl, className, rootClassName } = props;
  const classes = classNames(rootClassName || css.root, className);
  const gridRef = useRef(null);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Fetch category counts on mount using batched API (1 request instead of 8)
  useEffect(() => {
    const fetchCategoryCounts = async () => {
      const categories = [
        'cars-standard-listing',
        'porsche-race-cars-for-sale',
        'parts-listing',
        'porsche-books-art-for-sale',
        'watches',
        'clothing',
        'porsche-models-for-sale',
        'porsche-memorabilia-for-sale',
      ];

      try {
        // Single batched API call instead of 8 separate calls
        const response = await batchCategoryListingsCount(categories);
        setCategoryCounts(response.counts || {});
      } catch (error) {
        console.error('Error fetching category counts:', error);
        // Set all counts to 0 on error
        const fallbackCounts = categories.reduce((acc, cat) => {
          acc[cat] = 0;
          return acc;
        }, {});
        setCategoryCounts(fallbackCounts);
      }
    };

    fetchCategoryCounts();
  }, []);

  // Detect viewport size for mobile/tablet
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  const featuredItems = [
    {
      id: 'roadCars',
      image2: roadCarsIm2,
      titleId: 'FeaturedListing.roadCars',
      searchUrl: '?pub_categoryLevel1=cars-standard-listing',
      category: 'cars-standard-listing',
    },
    {
      id: 'raceCars',
      image2: raceCarsImg2,
      titleId: 'FeaturedListing.raceCars',
      searchUrl: '?pub_categoryLevel1=porsche-race-cars-for-sale',
      category: 'porsche-race-cars-for-sale',
    },
    {
      id: 'newUsedParts',
      image2: newUsedPartsImg2,
      titleId: 'FeaturedListing.newUsedParts',
      searchUrl: '?pub_categoryLevel1=parts-listing',
      category: 'parts-listing',
    },
    {
      id: 'booksArt',
      image2: booksArtImg2,
      titleId: 'FeaturedListing.booksArt',
      searchUrl: '?pub_categoryLevel1=porsche-books-art-for-sale',
      category: 'porsche-books-art-for-sale',
    },
    {
      id: 'porscheWatches',
      image2: porscheWatchesImg2,
      titleId: 'FeaturedListing.porscheWatches',
      searchUrl: '?pub_categoryLevel1=watches',
      category: 'watches',
    },
    {
      id: 'clothing',
      image2: clothingImg2,
      titleId: 'FeaturedListing.clothing',
      searchUrl: '?pub_categoryLevel1=clothing',
      category: 'clothing',
    },
    {
      id: 'modelToys',
      image2: modelToysImg2,
      titleId: 'FeaturedListing.modelToys',
      searchUrl: '?pub_categoryLevel1=porsche-models-for-sale',
      category: 'porsche-models-for-sale',
    },
    {
      id: 'memorabilia',
      image2: memorabiliaImg2,
      titleId: 'FeaturedListing.memorabilia',
      searchUrl: '?pub_categoryLevel1=porsche-memorabilia-for-sale',
      category: 'porsche-memorabilia-for-sale',
    },
  ];

  return (
    <div className={classes}>
      <h2 className={css.visuallyHidden}>Featured Categories</h2>
      <div className={css.grid} ref={gridRef}>
        {featuredItems.map(item => (
          <div key={item.id} className={css.gridItem}>
            <NamedLink
              name="SearchPage"
              to={{
                search: item.searchUrl,
              }}
              className={css.linkContainer}
            >
              <ParallaxItem
                image={item.image2}
                alt={intl.formatMessage({ id: item.titleId })}
              >
                <div className={css.parallaxContent}>
                  {categoryCounts[item.category] !== undefined && (
                    <div className={css.countBadge}>
                      {categoryCounts[item.category]} items
                    </div>
                  )}
                  <div className={css.titleOverlay}>
                    <h3 className={css.title}>{intl.formatMessage({ id: item.titleId })}</h3>
                  </div>
                </div>
              </ParallaxItem>
            </NamedLink>
          </div>
        ))}
      </div>
    </div>
  );
};

export default injectIntl(FeaturedListing);
